import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { UserContext } from '../contexts/UserContext';
import EventModal from '../components/modals/EventModal';
import { DateTime } from 'luxon';
import socket, { acquireSocketConnection, releaseSocketConnection } from '../../socket';
import '../components/Calendar.css';
import { getPageContainerStyles } from '../utils/styleUtils';
import { api as apiClient } from '../services/api';
import { hasAdminAccess } from '../utils/permissionConfig';
import { isOverlappingLimit } from '../components/Calendar/utils/calendarHelpers';
import { eventBlockAPI, loadMaximumConcurrentLimit } from '../utils/limitSettings';

const PARIS_TZ = 'Europe/Paris';

// 다국어 지원을 위한 TEXT 상수
const TEXT = {
  today: { ko: '오늘', en: 'Today', fr: 'Auj.' },
  month: { ko: '월', en: 'Month', fr: 'Mois' },
  week: { ko: '주', en: 'Week', fr: 'Sem.' },
  day: { ko: '일', en: 'Day', fr: 'Jour' },
  day15: { ko: '일15', en: 'Day15', fr: 'Jour15' },
  normalMode: { ko: '일반 모드', en: 'Normal Mode', fr: 'Mode normal' },
  offdayMode: { ko: '휴무 모드', en: 'Off-day Mode', fr: 'Mode congé' },
  defaultMode: { ko: '기본관리모드', en: 'Default Management Mode', fr: 'Mode gestion par défaut' },
  restrictedMode: { ko: '일정추가금지기간설정모드', en: 'Restricted Period Setting Mode', fr: 'Mode période interdite' },
  maxConcurrent: { ko: '동시일정제한갯수', en: 'Max Concurrent Events', fr: 'Max événements simultanés' },
  title: { ko: '관리자 캘린더 관리페이지', en: 'Admin Calendar Management', fr: 'Gestion calendrier admin' }
};

export default function AdminCalendarPage() {
  const { userInfo } = useContext(UserContext);
  const hasAdminPageAccess = hasAdminAccess(userInfo);
  const [mode, setMode] = useState('default'); // 'default' or 'restricted'
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const calendarRef = useRef();
  const calendarWrapperRef = useRef();
  const [maxConcurrent, setMaxConcurrent] = useState(3);

  // 언어 설정
  const lang = userInfo?.preferred_language || 'ko';

  // 현재 뷰 타입과 캘린더 타이틀 상태
  const [currentViewType, setCurrentViewType] = useState('dayGridMonth');
  const [calendarTitle, setCalendarTitle] = useState('');

  // 반짝이는 효과 상태
  const [shiningToday, setShiningToday] = useState(false);
  const [shiningDay15, setShiningDay15] = useState(false);

  // 직선 길이 계산 상태
  const [leftLineWidth, setLeftLineWidth] = useState(0);
  const [rightLineWidth, setRightLineWidth] = useState(0);
  const adjustTitleSizeTimeoutRef = useRef(null);

  // 공통 스타일 가져오기
  const pageContainerStyles = getPageContainerStyles();

  useEffect(() => {
    if (!hasAdminPageAccess) return;
    acquireSocketConnection();

    // 소켓 연결 및 실시간 반영 (통일된 socket 인스턴스 사용)
    const refetch = () => {
      if (calendarRef.current) {
        calendarRef.current.getApi().refetchEvents();
      }
    };
    socket.on('eventBlockAdded', refetch);
    socket.on('eventBlockDeleted', refetch);
    socket.on('eventBlockUpdated', refetch);
    socket.on('eventAdded', refetch);
    socket.on('eventUpdated', refetch);
    socket.on('eventDeleted', refetch);
    loadMaximumConcurrentLimit().then(setMaxConcurrent);
    return () => {
      socket.off('eventBlockAdded', refetch);
      socket.off('eventBlockDeleted', refetch);
      socket.off('eventBlockUpdated', refetch);
      socket.off('eventAdded', refetch);
      socket.off('eventUpdated', refetch);
      socket.off('eventDeleted', refetch);
      releaseSocketConnection();
    };
  }, [hasAdminPageAccess]);

  // 파리 타임존 변환 함수
  const toParisISO = (isoString) =>
    DateTime.fromISO(isoString, { zone: 'utc' }).setZone(PARIS_TZ).toISO();

  const handleModeChange = (e) => {
    setMode(e.target.value);
  };

  // 캘린더 날짜 선택시
  const handleDateSelect = (selectInfo) => {
    setSelectedEvent({
      start: selectInfo.startStr,
      end: selectInfo.endStr,
      allDay: selectInfo.allDay,
      is_restricted_period: mode === 'restricted',
    });
    setModalOpen(true);
  };

  // 이벤트 수정 시
  const handleEventChange = (changeInfo) => {
    const evt = {
      id: changeInfo.event.id,
      title: changeInfo.event.title,
      start: changeInfo.event.startStr || changeInfo.event.start,
      end: changeInfo.event.endStr || changeInfo.event.end,
      allDay: changeInfo.event.allDay,
      is_restricted_period: changeInfo.event.extendedProps.is_restricted_period || false,
    };

    // 금지기간은 기본관리모드에서 수정 불가
    if (evt.is_restricted_period && mode !== 'restricted') {
      alert('금지기간 이벤트는 기본관리모드에서 수정할 수 없습니다.');
      return;
    }

    setSelectedEvent(evt);
    setModalOpen(true);
  };

  // 헬퍼 함수 - 현재 미사용
  // eslint-disable-next-line no-unused-vars
  const safeTime = (val) => {
    if (!val) return null;
    return val.length === 10 ? val + "T00:00:00" : val;
  };

  // eslint-disable-next-line no-unused-vars
  const toParisISOString = (val) => {
    if (!val) return null;
    return DateTime.fromISO(val, { zone: 'Europe/Paris' }).toISO();
  };

  const handleMaxConcurrentChange = async (e) => {
    const value = Number(e.target.value);
    setMaxConcurrent(value);

    try {
      await apiClient.post('/calendar-settings', { maximum_rdv_par_jour: value });
    } catch (error) {
      alert(error?.data?.message || '동시일정 제한 저장에 실패했습니다.');
    }
  };

  // 모달 저장 핸들러
  const handleModalSave = async (payload) => {
    if (mode === 'restricted' || payload.is_restricted_period) {
      try {
        const requestBody = {
          start_time: payload.start,
          end_time: payload.end,
          reason: payload.reason || '금지 기간',
          created_by: userInfo.user_id,
        };

        if (selectedEvent?.id) {
          const blockId = String(selectedEvent.id).replace('block-', '');
          await eventBlockAPI.update(blockId, requestBody);
        } else {
          await eventBlockAPI.create(requestBody);
        }
        setModalOpen(false);
        setSelectedEvent(null);
      } catch (err) {
        alert('금지기간 저장 실패: ' + (err?.data?.message || err.message));
      }
    } else {
      const calendarApi = calendarRef.current?.getApi();
      const allEvents = calendarApi ? calendarApi.getEvents() : [];
      const excludeId = selectedEvent?.id && !selectedEvent?.is_restricted_period
        ? String(selectedEvent.id)
        : null;
      const overlapResult = await isOverlappingLimit(
        payload.start_time,
        payload.end_time,
        allEvents,
        excludeId,
        maxConcurrent,
        lang,
        { excludeAllDay: false }
      );

      if (overlapResult.isOverlapping) {
        alert(overlapResult.description || `해당 시간대에 이미 ${overlapResult.limit}개 이상의 일정이 있습니다.`);
        return;
      }

      try {
        await apiClient.post('/admin/events', payload);
        setModalOpen(false);
        setSelectedEvent(null);
      } catch (err) {
        alert('일정 저장 실패: ' + (err?.data?.message || err.message));
      }
    }
  };

  // 이벤트 클릭 핸들러
  const handleEventClick = (clickInfo) => {
    const isBlock = clickInfo.event.id?.toString().startsWith('block-');
    setSelectedEvent({
      id: clickInfo.event.id,
      title: clickInfo.event.title,
      start: clickInfo.event.startStr,
      end: clickInfo.event.endStr,
      allDay: clickInfo.event.allDay,
      is_restricted_period: isBlock || clickInfo.event.extendedProps?.is_restricted_period || false,
      reason: clickInfo.event.title,
    });
    setModalOpen(true);
  };

  // 모달 삭제 핸들러
  const handleModalDelete = async (eventId) => {
    if (selectedEvent?.is_restricted_period) {
      try {
        await eventBlockAPI.delete(eventId.replace('block-', ''));
        setModalOpen(false);
        setSelectedEvent(null);
      } catch {
        alert('금지기간 삭제 실패');
      }
    } else {
      try {
        await apiClient.delete(`/admin/events/${eventId}`);
        setModalOpen(false);
        setSelectedEvent(null);
      } catch {
        alert('일정 삭제 실패');
      }
    }
  };

  // 뷰 변경 핸들러
  const handleChangeView = (viewType) => {
    const api = calendarRef.current?.getApi();
    if (api) {
      api.changeView(viewType);
      setCurrentViewType(viewType);

      // 뷰별 설정 동적 적용
      setTimeout(() => {
        if (viewType === 'dayGridMonth') {
          // 월간 뷰: 요일만 표시
          api.setOption('dayHeaderFormat', { weekday: 'short' });
        } else if (viewType === 'timeGridDay') {
          // 일간 뷰: 요일 + 일 + 월 표시, 1시간 간격
          api.setOption('dayHeaderFormat', { weekday: 'short', day: 'numeric', month: 'short' });
          api.setOption('slotDuration', '01:00:00');
          api.setOption('slotLabelInterval', '01:00:00');
        } else if (viewType === 'timeGridWeek') {
          // 주간 뷰: 요일 + 일 표시, 1시간 간격
          api.setOption('dayHeaderFormat', { weekday: 'short', day: 'numeric' });
          api.setOption('slotDuration', '01:00:00');
          api.setOption('slotLabelInterval', '01:00:00');
        }
        adjustTitleSize();
      }, 100);
    }
  };

  // DAY15 전용 핸들러
  const handleDay15View = () => {
    const api = calendarRef.current?.getApi();
    if (api) {
      api.changeView('timeGridDay');
      setCurrentViewType('timeGridDay15');

      // 반짝이는 효과 시작 (3번 반복, 총 1초)
      setShiningDay15(true);
      setTimeout(() => setShiningDay15(false), 1000);

      setTimeout(() => {
        api.setOption('dayHeaderFormat', { weekday: 'short', day: 'numeric', month: 'short' });
        api.setOption('slotDuration', '00:15:00');
        api.setOption('slotLabelInterval', '00:15:00');
        adjustTitleSize();
      }, 100);
    }
  };

  // 이동 핸들러
  const handlePrev = () => {
    const api = calendarRef.current?.getApi();
    if (api) api.prev();
  };
  const handleNext = () => {
    const api = calendarRef.current?.getApi();
    if (api) api.next();
  };
  const handleToday = () => {
    const api = calendarRef.current?.getApi();
    if (api) api.today();

    // 반짝이는 효과 시작 (3번 반복, 총 1초)
    setShiningToday(true);
    setTimeout(() => setShiningToday(false), 1000);
  };

  // FullCalendar 뷰/날짜 변경 시 타이틀 갱신
  const handleDatesSet = (arg) => {
    setCurrentViewType(arg.view.type);
    setCalendarTitle(arg.view.title);
    // 날짜 변경 후 타이틀 크기 자동 조정 (디바운스 적용)
    adjustTitleSize();
  };

  // 직선 길이 계산 함수
  const calculateLineWidths = useCallback(() => {
    const viewportWidth = window.innerWidth;
    const tabContainer = document.querySelector('.calendar-tab-container');

    if (tabContainer) {
      const tabRect = tabContainer.getBoundingClientRect();
      const leftStart = tabRect.left; // 탭 컨테이너의 왼쪽 위치
      const rightEnd = viewportWidth - tabRect.right; // 뷰포트 끝에서 탭 컨테이너 오른쪽까지의 거리

      setLeftLineWidth(leftStart);
      setRightLineWidth(rightEnd);
    }
  }, []);

  // 타이틀 크기 자동 조정 함수 (디바운스 적용)
  const adjustTitleSize = useCallback(() => {
    // 이전 타이머가 있다면 취소
    if (adjustTitleSizeTimeoutRef.current) {
      clearTimeout(adjustTitleSizeTimeoutRef.current);
    }

    adjustTitleSizeTimeoutRef.current = setTimeout(() => {
      const titleElement = document.querySelector('.calendar-title-text');
      if (!titleElement) return;

      // 기본 크기로 리셋
      titleElement.style.fontSize = '1.7em';

      // 컨테이너 너비 확인
      const container = titleElement.parentElement;
      if (!container) return;

      const containerWidth = container.offsetWidth - 120; // 버튼 공간 제외
      if (containerWidth <= 0) return;

      // 한 번에 적절한 크기 계산
      const titleWidth = titleElement.scrollWidth;
      if (titleWidth > containerWidth) {
        const ratio = containerWidth / titleWidth;
        const newFontSize = Math.max(0.8, 1.7 * ratio);
        titleElement.style.fontSize = `${newFontSize}em`;
      }
    }, 150);
  }, []);

  // 컴포넌트 마운트 시와 윈도우 크기 변경 시 타이틀 크기 조정 및 직선 길이 계산
  useEffect(() => {
    const handleResize = () => {
      adjustTitleSize();
      calculateLineWidths();
    };

    // 초기 로드 시 타이틀 크기 조정 및 직선 길이 계산 (디바운스 적용)
    adjustTitleSize();

    // DOM이 렌더링된 후 직선 길이 계산
    const lineWidthTimeout = setTimeout(() => {
      calculateLineWidths();
    }, 100);

    // 윈도우 크기 변경 시 타이틀 크기 조정 및 직선 길이 계산
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(lineWidthTimeout);
      // 컴포넌트 언마운트 시 타이머 정리
      if (adjustTitleSizeTimeoutRef.current) {
        clearTimeout(adjustTitleSizeTimeoutRef.current);
      }
    };
  }, [adjustTitleSize, calculateLineWidths]);

  if (!hasAdminPageAccess) {
    return (
      <div className={pageContainerStyles.pageContainer}>
        <h1 className={pageContainerStyles.pageTitle}>
          {TEXT.title[lang]}
        </h1>
        <p>관리자 권한이 필요합니다.</p>
      </div>
    );
  }

  return (
    <div className={pageContainerStyles.pageContainer}>
      {/* 페이지 타이틀 */}
      <h1 className={pageContainerStyles.pageTitle}>
        {TEXT.title[lang]}
      </h1>

      <div>
        <label>
          <input
            type="radio"
            value="default"
            checked={mode === 'default'}
            onChange={handleModeChange}
          />
          {TEXT.defaultMode[lang]}
        </label>
        <label style={{ marginLeft: 20 }}>
          <input
            type="radio"
            value="restricted"
            checked={mode === 'restricted'}
            onChange={handleModeChange}
          />
          {TEXT.restrictedMode[lang]}
        </label>
      </div>

      <div style={{ marginBottom: 10 }}>
        <label>
          {TEXT.maxConcurrent[lang]}:&nbsp;
          <input
            type="number"
            min={1}
            value={maxConcurrent}
            onChange={handleMaxConcurrentChange}
            style={{ width: 60 }}
          /> 개
        </label>
      </div>

      <div className="calendar-wrapper" ref={calendarWrapperRef}>
        {/* 커스텀 캘린더 헤더 */}
        <div className="calendar-custom-header">
          <div className="calendar-title-row">
            <button className="calendar-nav-btn" onClick={handlePrev}>&lt;</button>
            <span className="calendar-title-text" style={{ fontWeight: 300 }}>{calendarTitle}</span>
            <button className="calendar-nav-btn" onClick={handleNext}>&gt;</button>
          </div>
          {/* 탭 네비게이션 */}
          <div style={{
            display: 'flex',
            marginBottom: '5px',
            gap: '0',
            justifyContent: 'center',
            position: 'relative'
          }}>
            {/* 왼쪽 직선 */}
            <div style={{
              position: 'absolute',
              left: `-${leftLineWidth}px`,
              top: '50%',
              transform: 'translateY(-50%)',
              width: `${leftLineWidth}px`,
              height: '1px',
              backgroundColor: '#000',
              zIndex: '10'
            }} />

            {/* 오른쪽 직선 */}
            <div style={{
              position: 'absolute',
              right: `-${rightLineWidth}px`,
              top: '50%',
              transform: 'translateY(-50%)',
              width: `${rightLineWidth}px`,
              height: '1px',
              backgroundColor: '#000',
              zIndex: '10'
            }} />

            {/* 탭 컨테이너 */}
            <div
              className="calendar-tab-container"
              style={{
                display: 'flex',
                gap: '0',
                justifyContent: 'center',
                border: '1px solid #000',
                borderRadius: '8px',
                padding: '0'
              }}
            >

              <button
                onClick={handleToday}
                style={{
                  padding: '4px 16px',
                  border: '1px solid transparent',
                  backgroundColor: '#fff',
                  color: '#000',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '400',
                  borderTopLeftRadius: '8px',
                  borderBottomLeftRadius: '8px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  borderRight: '1px solid transparent',
                  transform: 'scale(1)',
                  zIndex: '1',
                  animation: shiningToday ? 'tabShine 0.33s ease-in-out 3' : 'none'
                }}
              >
                {TEXT.today[lang]}
              </button>
              <button
                onClick={() => handleChangeView('dayGridMonth')}
                style={{
                  padding: '4px 16px',
                  border: currentViewType === 'dayGridMonth' ? '1px solid #000' : '1px solid transparent',
                  backgroundColor: currentViewType === 'dayGridMonth' ? '#fff' : '#fff',
                  color: currentViewType === 'dayGridMonth' ? '#000' : '#000',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '400',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  borderLeft: currentViewType === 'dayGridMonth' ? '1px solid #000' : '1px solid transparent',
                  borderRight: currentViewType === 'dayGridMonth' ? '1px solid #000' : '1px solid transparent',
                  transform: currentViewType === 'dayGridMonth' ? 'scale(1.15)' : 'scale(1)',
                  zIndex: currentViewType === 'dayGridMonth' ? '2' : '1'
                }}
              >
                {TEXT.month[lang]}
              </button>
              <button
                onClick={() => handleChangeView('timeGridWeek')}
                style={{
                  padding: '4px 16px',
                  border: currentViewType === 'timeGridWeek' ? '1px solid #000' : '1px solid transparent',
                  backgroundColor: currentViewType === 'timeGridWeek' ? '#fff' : '#fff',
                  color: currentViewType === 'timeGridWeek' ? '#000' : '#000',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '400',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  borderLeft: currentViewType === 'timeGridWeek' ? '1px solid #000' : '1px solid transparent',
                  borderRight: currentViewType === 'timeGridWeek' ? '1px solid #000' : '1px solid transparent',
                  transform: currentViewType === 'timeGridWeek' ? 'scale(1.15)' : 'scale(1)',
                  zIndex: currentViewType === 'timeGridWeek' ? '2' : '1'
                }}
              >
                {TEXT.week[lang]}
              </button>
              <button
                onClick={() => handleChangeView('timeGridDay')}
                style={{
                  padding: '4px 16px',
                  border: currentViewType === 'timeGridDay' && currentViewType !== 'timeGridDay15' ? '1px solid #000' : '1px solid transparent',
                  backgroundColor: currentViewType === 'timeGridDay' && currentViewType !== 'timeGridDay15' ? '#fff' : '#fff',
                  color: currentViewType === 'timeGridDay' && currentViewType !== 'timeGridDay15' ? '#000' : '#000',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '400',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  borderLeft: currentViewType === 'timeGridDay' && currentViewType !== 'timeGridDay15' ? '1px solid #000' : '1px solid transparent',
                  borderRight: currentViewType === 'timeGridDay' && currentViewType !== 'timeGridDay15' ? '1px solid #000' : '1px solid transparent',
                  transform: currentViewType === 'timeGridDay' && currentViewType !== 'timeGridDay15' ? 'scale(1.15)' : 'scale(1)',
                  zIndex: currentViewType === 'timeGridDay' && currentViewType !== 'timeGridDay15' ? '2' : '1'
                }}
              >
                {TEXT.day[lang]}
              </button>
              <button
                onClick={handleDay15View}
                style={{
                  padding: '4px 16px',
                  border: currentViewType === 'timeGridDay15' ? '1px solid #000' : '1px solid transparent',
                  backgroundColor: currentViewType === 'timeGridDay15' ? '#fff' : '#fff',
                  color: currentViewType === 'timeGridDay15' ? '#000' : '#000',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '400',
                  borderTopRightRadius: '8px',
                  borderBottomRightRadius: '8px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  borderLeft: currentViewType === 'timeGridDay15' ? '1px solid #000' : '1px solid transparent',
                  transform: currentViewType === 'timeGridDay15' ? 'scale(1.15)' : 'scale(1)',
                  zIndex: currentViewType === 'timeGridDay15' ? '2' : '1',
                  animation: shiningDay15 ? 'tabShine 0.33s ease-in-out 3' : 'none'
                }}
              >
                {TEXT.day15[lang]}
              </button>
            </div>
          </div>
        </div>

        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={false}
          timeZone="Europe/Paris"
          locale="fr"
          slotLabelFormat={{
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }}
          selectable={true}
          editable={mode === 'default'}
          events={(fetchInfo, successCallback, failureCallback) => {
            apiClient.get('/admin/events', {
              start: fetchInfo.startStr,
              end: fetchInfo.endStr,
            })
              .then(data => {
                const mappedEvents = data.events.map(e => ({
                  ...e,
                  id: String(e.id),
                  start: toParisISO(e.start_time),
                  end: toParisISO(e.end_time),
                  allDay: e.is_all_day,
                  backgroundColor: e.user_color || '#1976d2',
                  borderColor: e.user_color || '#1976d2',
                }));
                // 금지기간도 같이 불러오기
                apiClient.get('/admin/event-blocks', {
                  start: fetchInfo.startStr,
                  end: fetchInfo.endStr,
                })
                  .then(blockData => {
                    const mappedBlocks = (blockData.eventBlocks || []).map(block => {
                      const startDate = new Date(toParisISO(block.start_time));
                      const endDate = new Date(toParisISO(block.end_time));
                      const isAllDay =
                        startDate.getHours() === 0 && startDate.getMinutes() === 0 && startDate.getSeconds() === 0 &&
                        endDate.getHours() === 0 && endDate.getMinutes() === 0 && endDate.getSeconds() === 0 &&
                        (endDate.getTime() - startDate.getTime()) % (24 * 60 * 60 * 1000) === 0;
                      return {
                        ...block,
                        id: `block-${block.id}`,
                        start: toParisISO(block.start_time),
                        end: toParisISO(block.end_time),
                        allDay: isAllDay,
                        backgroundColor: '#a9a9a9',
                        borderColor: '#a9a9a9',
                        editable: false,
                        is_restricted_period: true,
                      };
                    });
                    successCallback([...mappedEvents, ...mappedBlocks]);
                  })
                  .catch(failureCallback);
              })
              .catch(failureCallback);
          }}
          select={handleDateSelect}
          eventChange={handleEventChange}
          eventClick={handleEventClick}
          datesSet={handleDatesSet}
        />
      </div>

      {modalOpen && (
        <EventModal
          show={modalOpen}
          eventData={selectedEvent}
          mode={mode}
          onSave={handleModalSave}
          onDelete={handleModalDelete}
          onClose={() => {
            setModalOpen(false);
            setSelectedEvent(null);
          }}
        />
      )}

      <hr />
      <p>———————기능설명———————</p>
      <p>기본관리모드에서는 모든 일정 추가/수정/삭제가 가능합니다.</p>
      <p>일정추가금지기간설정모드에서는 추가/수정 일정이 금지기간으로 등록되어 다른 회원들이 이 기간에 일정을 추가할 수 없습니다.</p>

      {/* 하단 여백 */}
      <div style={{ height: '50px' }}></div>
    </div>
  );
}
